import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bookmark, Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverAnchor, PopoverArrow, PopoverContent } from '@/components/ui/popover';
import { kluczPodpowiedziFlagi, kluczPodpowiedziZapisu } from '@/lib/mojeFiltry';
import type { MojeFiltryStrony } from '@/hooks/useMojeFiltry';

/** Jak długo wisi „Wybierz cenę, żeby zapisać” po dotknięciu szarego „Zapisz” na telefonie (makieta: 1,8 s). */
export const CZAS_DYMKA_CENY_MS = 1800;
/** Flaga „wraca” po ptaszku: pop 300 ms, klasa zdjęta po 400 ms (makieta: `.relight`). */
export const CZAS_ZAPALENIA_MS = 400;

/**
 * Ruch z makiet (`serp-saved-filters-proto*.html`): „Zapisz” wysuwa się i chowa przez `max-width`
 * 0 ↔ 140 px (pulpit) / 90 px (telefon) w 250 ms `ease`; ptaszek i powrót flagi to `pop` (skala
 * 0,5 → 1 i przezroczystość 0 → 1) w 280 / 300 ms `ease`.
 *
 * Widoczny ruch trwa tyle, ile `max-width` potrzebuje, żeby dojść do szerokości treści. W makiecie
 * treść zajmuje ~51 % (pulpit, 72 z 140 px) i ~66 % (telefon, 59 z 90 px) celu, czyli ruch widać
 * przez ~90 / ~115 ms. Nasze „Zapisz” jest węższe i ma inne długości w ua/by, więc cel liczymy z
 * ZMIERZONEJ treści w tej samej proporcji — ruch wygląda jak w makiecie w każdym języku.
 */
export const UDZIAL_TRESCI_PULPIT = 72 / 140;
export const UDZIAL_TRESCI_TELEFON = 59 / 90;
const WYSUNIECIE = 'transition-[max-width] duration-250 ease-css motion-reduce:transition-none';
const POP_PTASZKA = 'animate-in zoom-in-50 fade-in-0 duration-280 ease-css motion-reduce:animate-none';
const POP_FLAGI = 'animate-in zoom-in-50 fade-in-0 duration-300 ease-css motion-reduce:animate-none';
/**
 * Tło, zaokrąglenie i odstępy połów przechodzą w tym samym tempie co chowanie „Zapisz” — inaczej
 * w chwili zapalenia flaga robiła się okrągła w jednej klatce, obok wciąż szerokiego „Zapisz”
 * (biały „wgryz” między dwoma zielonymi polami), a przy pierwszym zapisie ikona skakała o 11 px
 * (zmiana odstępów jednego przycisku na dwie połowy). Maciek 19.10: „to mruga”.
 */
const PRZEJSCIE_POLOWY = 'transition-[color,background-color,padding,margin,width,border-radius] duration-250 ease-css motion-reduce:transition-none';
/**
 * Zapalona flaga przy schowanym „Zapisz” (pulpit) zaokrągla się OD RAZU: kanciasta po chowaniu
 * (`chowaZapalona`), a przycięcie połów znika razem z końcem chowania — promień rosnący 250 ms
 * wystawiał zielony róg poza obramowanie (Maciek 20.10).
 */
const PRZEJSCIE_POLOWY_BEZ_PROMIENIA = 'transition-[color,background-color,padding,margin,width] duration-250 ease-css motion-reduce:transition-none';
/** Podpowiedź przy przejściu na sąsiada znika od razu (nigdy dwie naraz), także „Wszystkie” w RzadFiltrow. */
export const BEZ_WYJSCIA = 'data-[state=closed]:!animate-none';
/** Ile trwa chowanie „Zapisz” (`WYSUNIECIE`) — do końca tego czasu hover i podpowiedzi milczą. */
export const CZAS_CHOWANIA_MS = 250;

/**
 * FLAGA „MOJE FILTRY” I „ZAPISZ” (build plan C1, plan E1.3, §1.2–1.9, §1.71; makiety
 * `serp-saved-filters-proto.html` i `-mobile.html`).
 *
 * Pulpit: prawa część przycisku dzielonego [Wszystkie | flaga | Zapisz] — `RzadFiltrow` rysuje
 * „Wszystkie” i obramowanie, ten komponent kreskę, flagę i wysuwane „Zapisz”. Telefon: własna
 * pigułka [flaga | Zapisz] za okrągłym przyciskiem filtrów.
 *
 * Przed pierwszym zapisem flaga i „Zapisz” są JEDNYM przyciskiem (§1.71): bez kreski, wspólne
 * podświetlenie i jedna podpowiedź, dotknięcie flagi też zapisuje. Bez ceny całość jest szara,
 * a dotknięcie pokazuje „Wybierz cenę, żeby zapisać” (na telefonie nie ma hovera — nigdy cisza).
 */
export function ZakladkaMojichFiltrow({ stan, kompakt = false }: { stan: MojeFiltryStrony; kompakt?: boolean }) {
  const { t } = useTranslation();
  const [dymekCeny, setDymekCeny] = useState(false);
  const czasomierz = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(czasomierz.current), []);
  // Koniec ptaszka = flaga wraca z „pop” (makieta: `.relight`). Tylko po zapisie, nie przy wejściu na stronę.
  const [zapala, setZapala] = useState(false);
  const bylPtaszek = useRef(stan.sprawdza);
  useEffect(() => {
    const byl = bylPtaszek.current;
    bylPtaszek.current = stan.sprawdza;
    if (!byl || stan.sprawdza) return;
    setZapala(true);
    const id = window.setTimeout(() => setZapala(false), CZAS_ZAPALENIA_MS);
    return () => window.clearTimeout(id);
  }, [stan.sprawdza]);
  const [nadGrupa, setNadGrupa] = useState(false);

  // „Zapisz” się chowa (koniec przejścia `max-width`): ruch trwa, hover i podpowiedzi milczą.
  const [chowa, setChowa] = useState(false);
  const bylZapisz = useRef(stan.pokazZapisz);
  useEffect(() => {
    const byl = bylZapisz.current;
    bylZapisz.current = stan.pokazZapisz;
    if (!byl || stan.pokazZapisz) return;
    setChowa(true);
    const id = window.setTimeout(() => setChowa(false), CZAS_CHOWANIA_MS);
    return () => window.clearTimeout(id);
  }, [stan.pokazZapisz]);

  /*
   * WYCISZENIE PO KLIKNIĘCIU (pulpit, Maciek 19.10). Kursor stoi na „Zapisz” albo na fladze, a pod
   * nim przez sekundę dzieje się ptaszek → zapalenie → chowanie; przycisk dzielony jest przypięty do
   * prawej, więc po schowaniu „Zapisz” pod nieruchomy kursor wjeżdża flaga albo „Wszystkie”. Każdy
   * `:hover` po drodze (szare tło flagi przechodzące w zielone, zielone tło „Zapisz” obcięte w
   * połowie, szare „Wszystkie”) i każda podpowiedź to mrugnięcie, którego telefon (bez hovera) nie ma.
   * Dlatego od kliknięcia hover i podpowiedzi milczą, aż ruch się skończy I kursor się naprawdę
   * poruszy (sztuczne `pointermove` przeglądarki po zmianie układu mają ruch 0 i nie liczą się).
   */
  const [wyciszone, setWyciszone] = useState(false);
  const spokoj = !stan.sprawdza && !stan.przekreslone && !chowa;
  const spokojRef = useRef(spokoj);
  spokojRef.current = spokoj;
  useEffect(() => {
    if (!wyciszone) return;
    const naRuch = (e: PointerEvent) => {
      if (spokojRef.current && (e.movementX !== 0 || e.movementY !== 0)) setWyciszone(false);
    };
    document.addEventListener('pointermove', naRuch);
    return () => document.removeEventListener('pointermove', naRuch);
  }, [wyciszone]);
  const wycisz = () => { if (!kompakt) setWyciszone(true); };
  // Szerokość treści segmentu „Zapisz” (scrollWidth nie zależy od `max-width`), do celu przejścia.
  const segment = useRef<HTMLSpanElement>(null);
  const [szerTresci, setSzerTresci] = useState<number | null>(null);
  const zmierzSegment = useCallback(() => {
    const w = segment.current?.scrollWidth;
    if (w) setSzerTresci((p) => (p === w ? p : w));
  }, []);
  // Bez tablicy zależności CELOWO (jak `ScrollableRow`): zmiana języka albo stanu zmienia treść;
  // stan zmienia się tylko przy faktycznej różnicy, więc pomiar ustala się od razu.
  useLayoutEffect(zmierzSegment);
  const [podpowiedzZapisu, setPodpowiedzZapisu] = useState(false);
  const pokazDymekCeny = () => {
    setDymekCeny(true);
    window.clearTimeout(czasomierz.current);
    czasomierz.current = window.setTimeout(() => setDymekCeny(false), CZAS_DYMKA_CENY_MS);
  };
  const zapisz = () => {
    // Bez ceny zapis nie rusza: nic się nie animuje, więc podpowiedź „Wybierz cenę…” zostaje.
    if (stan.naZapisz()) wycisz();
    else pokazDymekCeny();
  };
  const naFlage = () => {
    if (stan.jedenPrzycisk) { zapisz(); return; }
    stan.naFlage();
    wycisz();
  };

  const jeden = stan.jedenPrzycisk;
  const szary = !stan.mozeZapisac;
  const zapalona = stan.zapalona || stan.sprawdza;
  // „Zapisz” chowa się za ZAPALONĄ flagą (po zapisie albo po „wróć do moich filtrów”): na ten czas
  // pulpit przycina połowy do zaokrąglenia przycisku. Zapalona flaga nie ma znaczka „+N”, więc nic
  // nie zostaje ucięte.
  const chowaZapalona = !kompakt && chowa && zapalona;
  // Pulpit: połowy WYPEŁNIAJĄ obramowanie przycisku dzielonego (`h-10` z ramką = 38 px w środku).
  // Własne `h-10` wystawało tło o 2 px pod ramkę (Maciek 18.10).
  const wys = kompakt ? 'h-8' : 'self-stretch';
  const tekstFlagi = jeden ? t(kluczPodpowiedziZapisu(stan)) : t(kluczPodpowiedziFlagi(stan), { set: stan.opis });
  // „N nowych ofert” dopisujemy tylko do „Włącz moje filtry: …” — przy wysuniętym „Zapisz” flaga znaczy „wróć”.
  const tekstNowych = !jeden && !stan.zapalona && !stan.pokazZapisz && stan.nowe > 0 ? t('search.my_filters.tip_new', { count: stan.nowe }) : null;

  const flaga = (
    <button
      type="button"
      onClick={naFlage}
      data-testid="flaga-moich-filtrow"
      data-zapalona={stan.zapalona ? 'tak' : 'nie'}
      data-stan={jeden ? 'pierwszy' : stan.zapalona ? 'zapalona' : stan.pokazZapisz ? 'edytowana' : 'zgaszona'}
      aria-pressed={jeden ? undefined : stan.zapalona}
      aria-label={jeden ? t(kluczPodpowiedziZapisu(stan)) : t('search.my_filters.flag_aria')}
      aria-disabled={jeden && szary ? true : undefined}
      className={cn(
        'strefa-dotyku relative flex shrink-0 items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        !kompakt && zapalona && !stan.pokazZapisz ? PRZEJSCIE_POLOWY_BEZ_PROMIENIA : PRZEJSCIE_POLOWY,
        wys,
        // Telefon, jeden przycisk: `w-8 pl-1` zamiast `ml-1 w-7` — ikona stoi w tym samym miejscu, a zielone
        // tło ptaszka jest kółkiem przy lewym końcu pigułki jak przy zapisie zmian, nie kanciastym blokiem (Maciek 20.10).
        kompakt ? (jeden ? 'w-8 rounded-2xl pl-1' : 'w-8 rounded-2xl') : jeden ? 'pl-3 pr-0.5' : 'px-3',
        // Promień konkretny (połowa wysokości), nie `rounded-r-full`: 9999 px przechodzi w 0 w jednej klatce.
        // Dopiero PO schowaniu „Zapisz”: zaokrąglona wcześniej flaga zostawiała za rogiem pasek chowanego tła.
        !kompakt && !stan.pokazZapisz && !chowaZapalona && 'rounded-r-[20px]',
        jeden
          ? szary ? cn('text-muted-foreground', kompakt ? 'opacity-70' : 'opacity-60') : cn('text-primary', stan.sprawdza && 'bg-primary/10')
          : zapalona ? 'bg-primary/10 text-primary' : cn('text-muted-foreground', !wyciszone && 'hover:bg-muted'),
        !kompakt && jeden && !szary && !wyciszone && 'group-hover/jeden:bg-primary/10',
      )}
    >
      {stan.sprawdza ? (
        <Check className={cn(POP_PTASZKA, kompakt ? 'h-4 w-4' : 'h-[18px] w-[18px]')} strokeWidth={2.6} aria-hidden />
      ) : (
        <Bookmark
          data-zapala={zapala ? 'tak' : undefined}
          className={cn(kompakt ? 'h-4 w-4' : 'h-[18px] w-[18px]', zapalona && !jeden && 'fill-current', zapala && POP_FLAGI)}
          aria-hidden
        />
      )}
      {!jeden && !stan.zapalona && stan.nowe > 0 && (
        <span
          data-testid="znaczek-nowych"
          className={cn(
            'absolute inline-flex items-center justify-center rounded-full bg-primary font-bold text-primary-foreground ring-2 ring-background',
            // Makiety: pulpit 20 px / 11 px, telefon 18 px / 10 px.
            kompakt ? '-right-[7px] -top-[7px] h-[18px] min-w-[18px] px-1 text-[10px]' : '-right-1.5 -top-[7px] h-5 min-w-5 px-1 text-[11px]',
          )}
        >
          +{stan.nowe}
        </span>
      )}
    </button>
  );

  const przyciskZapisu = (
    <button
      type="button"
      onClick={zapisz}
      tabIndex={stan.pokazZapisz ? 0 : -1}
      aria-hidden={stan.pokazZapisz ? undefined : true}
      aria-disabled={szary || undefined}
      aria-invalid={stan.blad || undefined}
      // Nazwa = widoczny napis („Zapisz”), podpowiedź jako opis (audyt 19.10): nazwa „Wybierz cenę,
      // żeby zapisać” nie zawierała słowa z przycisku, a sterowanie głosem szuka po tym, co widać.
      aria-description={t(kluczPodpowiedziZapisu(stan))}
      data-testid="zapisz-moje-filtry"
      data-szary={szary ? 'tak' : 'nie'}
      className={cn(
        'strefa-dotyku relative flex shrink-0 items-center whitespace-nowrap font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        PRZEJSCIE_POLOWY,
        kompakt ? 'h-8 rounded-r-full text-xs' : 'self-stretch rounded-r-full text-sm',
        kompakt ? (jeden ? 'pl-1 pr-3' : 'pl-2.5 pr-3') : jeden ? 'pl-1.5 pr-4' : 'px-3.5',
        szary || stan.przekreslone ? 'cursor-default text-muted-foreground' : stan.blad ? 'text-destructive' : 'text-primary',
        // Pulpit, ptaszek po kliknięciu i chowanie po zapisie: „Zapisz” ma to samo zielone tło co flaga.
        // Bez tego tło kończyło się w połowie przycisku (Maciek 20.10). Telefon: zielona jest cała pigułka.
        !kompakt && zapalona && (stan.pokazZapisz || chowa) && 'bg-primary/10',
        !szary && !stan.przekreslone && !jeden && !wyciszone && 'hover:bg-primary/10',
        !kompakt && jeden && !szary && !wyciszone && 'group-hover/jeden:bg-primary/10',
      )}
    >
      <span className={cn(szary && 'opacity-60')}>{t('search.my_filters.save')}</span>
      {/* „Nie zapisuj, wróć do Moich filtrów”: kreska przez „Zapisz”, zanim się schowa. */}
      <span
        aria-hidden
        data-testid="przekreslenie-zapisu"
        // Kreska rysuje się 300 ms `ease` (makieta: `@keyframes strike`) i znika od razu razem z klasą — nie cofa się.
        className={cn(
          'pointer-events-none absolute left-2 right-2 top-1/2 h-0.5 -translate-y-1/2 origin-left rounded-full bg-current',
          stan.przekreslone ? 'scale-x-100 transition-transform duration-300 ease-css motion-reduce:transition-none' : 'scale-x-0',
        )}
      />
    </button>
  );

  const segmentZapisu = (
    <span
      ref={segment}
      data-testid="segment-zapisu"
      data-widoczny={stan.pokazZapisz ? 'tak' : 'nie'}
      className={cn('relative inline-flex items-stretch overflow-hidden rounded-r-full', WYSUNIECIE, stan.pokazZapisz ? (kompakt ? 'max-w-[90px]' : 'max-w-[140px]') : 'max-w-0')}
      style={stan.pokazZapisz && szerTresci ? { maxWidth: Math.ceil(szerTresci / (kompakt ? UDZIAL_TRESCI_TELEFON : UDZIAL_TRESCI_PULPIT)) } : undefined}
    >
      {/* Kreska LEŻY NA tle (absolute), nie jest osobną kolumną: tło hover/zapalenia sięga pod nią, bez białych szczelin nad i pod kreską. */}
      {/* Kreska jest zawsze w DOM i GAŚNIE razem z chowaniem: znikała dopiero przy 0 px i do końca
          stała jako samotna pionowa kreska za zaokrągloną flagą (19.10). */}
      <span
        aria-hidden
        data-testid="kreska-zapisu"
        className={cn(
          'pointer-events-none absolute left-0 top-1/2 z-10 w-px -translate-y-1/2 bg-border transition-opacity duration-250 ease-css motion-reduce:transition-none',
          kompakt ? 'h-[18px]' : 'h-7',
          !jeden && stan.pokazZapisz ? 'opacity-100' : 'opacity-0',
        )}
      />
      {przyciskZapisu}
    </span>
  );

  // Pulpit: podpowiedzi na hover. DRZEWO MUSI BYĆ STAŁE: „Zapisz” wysuwa się przejściem
  // `max-width`, a przejście działa tylko na elemencie, który zostaje w DOM. Owijanie segmentu w
  // `Tooltip` tylko wtedy, gdy był wysunięty, i inny układ przy pierwszym zapisie montowały go od
  // nowa w stanie końcowym — „Zapisz” pojawiało się i znikało bez ruchu (Maciek 16.10). Dlatego
  // każda połowa ma swój `Tooltip` zawsze, a wyłączona podpowiedź po prostu nie ma treści.
  // Jeden przycisk (§1.71): wskazanie którejkolwiek połowy pokazuje JEDNĄ podpowiedź, tę przy „Zapisz”.
  // Jednorazowy dymek wygrywa z podpowiedzią na hover (makieta: `*:has(> .hint.show) .bm .tip`).
  const zDymkiem = !!stan.dymek;
  const naPulpicie = (
    el: React.ReactElement,
    tekst: string | null,
    dopisek?: string | null,
    sterowanie?: { open: boolean; onOpenChange: (o: boolean) => void },
  ) =>
    kompakt ? el : (
      // `disableHoverableContent`: treść podpowiedzi (wyrównana do prawej) leży POD sąsiednią połową,
      // więc Radix traktował przejście z „Zapisz” na flagę jako „w drodze do treści” — stara podpowiedź
      // zostawała, a flaga nie dostawała swojej (Maciek 19.10). Bez tego sąsiad otwiera się od razu.
      <Tooltip disableHoverableContent {...sterowanie}>
        <TooltipTrigger asChild>{el}</TooltipTrigger>
        {tekst && !zDymkiem && !wyciszone && (
          // Bez animacji zamykania: przy przejściu na sąsiada stara podpowiedź znika od razu, nie gaśnie pod nową.
          <TooltipContent side="bottom" align="end" className={BEZ_WYJSCIA} data-testid="podpowiedz-moich-filtrow">
            {tekst}
            {dopisek && <span> · {dopisek}</span>}
          </TooltipContent>
        )}
      </Tooltip>
    );

  const tresc = (
    <span
      className={cn('inline-flex items-stretch', jeden && 'group/jeden')}
      data-testid={jeden ? 'jeden-przycisk-zapisu' : undefined}
      onPointerEnter={() => setNadGrupa(true)}
      onPointerLeave={() => setNadGrupa(false)}
    >
      {naPulpicie(flaga, jeden ? null : tekstFlagi, tekstNowych)}
      {naPulpicie(
        segmentZapisu,
        jeden || stan.pokazZapisz ? t(kluczPodpowiedziZapisu(stan)) : null,
        null,
        // Jeden przycisk: podpowiedź idzie za wskazaniem CAŁOŚCI i nie zostawia otwartego stanu na
        // potem (inaczej po pierwszym zapisie wisiałaby przy flagi obok jej własnej).
        { open: !wyciszone && (jeden ? nadGrupa : podpowiedzZapisu && stan.pokazZapisz), onOpenChange: (o) => setPodpowiedzZapisu(o && !jeden) },
      )}
    </span>
  );

  const calosc = kompakt ? (
    <span
      data-testid="pigulka-moich-filtrow"
      className={cn(
        'relative inline-flex shrink-0 items-center rounded-full border bg-white transition-colors duration-200',
        (zapalona && !jeden) || stan.sprawdza ? 'border-primary bg-primary/10' : stan.pokazZapisz && !szary ? 'border-primary' : 'border-input',
      )}
    >
      {tresc}
    </span>
  ) : (
    // `data-wyciszone`: „Wszystkie” obok (RzadFiltrow) też nie podświetla się, gdy wjedzie pod kursor.
    // `chowaZapalona`: kurczący się segment nie wystaje kanciastym paskiem za róg (Maciek 20.10).
    <span
      className={cn('relative inline-flex items-stretch', chowaZapalona && 'overflow-hidden rounded-r-[20px]')}
      data-testid="polowy-moich-filtrow"
      data-wyciszone={wyciszone ? 'tak' : undefined}
    >
      {/* Kreska między „Wszystkie” a flagą jest zawsze; między flagą a „Zapisz” — tylko po pierwszym zapisie.
          Leży NA tle (absolute), więc hover „Wszystkie” i zapalona flaga wypełniają segment aż do niej (Maciek 18.10). */}
      <span aria-hidden className="pointer-events-none absolute left-0 top-1/2 z-10 h-7 w-px -translate-y-1/2 bg-border" />
      {tresc}
    </span>
  );

  // Dymek jednorazowy (§1.8) i „Wybierz cenę…” po dotknięciu szarego przycisku: przy fladze, portal.
  const tekstDymka = stan.dymek ? t(stan.dymek) : null;
  return (
    <Popover open={!!tekstDymka || dymekCeny} onOpenChange={(o) => { if (!o) { setDymekCeny(false); if (tekstDymka) stan.zamknijDymek(); } }}>
      <PopoverAnchor asChild>{calosc}</PopoverAnchor>
      <PopoverContent
        side="bottom"
        align="end"
        sideOffset={8}
        data-testid={tekstDymka ? 'dymek-moich-filtrow' : 'dymek-ceny'}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        className={cn(
          'rounded-xl border-0 bg-foreground text-background shadow-md',
          tekstDymka ? 'w-72 max-w-[calc(100vw-2rem)] py-2.5 pl-3 pr-9 text-sm' : 'w-auto px-3 py-1.5 text-xs',
        )}
      >
        {tekstDymka ? (
          <>
            <b className="mb-0.5 block">{t('search.my_filters.name')}</b>
            <span>{tekstDymka}</span>
            <button
              type="button"
              onClick={stan.zamknijDymek}
              aria-label={t('search.my_filters.hint_close')}
              className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full opacity-75 hover:bg-background/15 hover:opacity-100"
            >
              <X className="h-4 w-4" />
            </button>
          </>
        ) : (
          t('search.my_filters.tip_need_price')
        )}
        <PopoverArrow className="fill-foreground" />
      </PopoverContent>
    </Popover>
  );
}

export default ZakladkaMojichFiltrow;
